/* BP mobile chrome.
   1) Hides the floating bars while scrolling down; restores them on scroll up.
   2) Keeps the page's top reserve equal to the pill's real height.
   3) Opens the burger's menu sheet (snippets/bp-menu.liquid), which renders the SAME link
      list as the desktop header. It used to forward the tap to Showcase's own #page-menu
      instead, and that sheet reads the BASE THEME's menu setting — so the phone showed a
      different menu from the desktop. The theme's sheet is still the fallback if ours is
      somehow not on the page.
   4) Lifts third-party corner widgets (the Shopify Inbox chat bubble, and anything else
      that pins itself bottom-right) clear of the tab bar.

   Above 1100px all of this is inert — nothing is floating up there. */
(function () {
  if (window.__bpChromeInit) return;
  window.__bpChromeInit = true;

  var THRESHOLD = 60;   /* px scrolled before hiding is allowed at all */
  var DELTA = 5;        /* ignore sub-pixel jitter / rubber-banding */
  var HIDDEN = 'bp-chrome-hidden';
  var BREAK = 1100;

  /* ------------------------------------------------------------------
     Liquid glass refraction — switched on only where it actually works.

     Referencing an SVG filter from backdrop-filter is a Chromium capability. Safari does
     not merely skip it: meeting a url() it cannot resolve invalidates the WHOLE
     backdrop-filter declaration, so writing it unconditionally would cost every iPhone the
     blur and saturation too and leave a flat tinted bar.

     Hence the property is empty in the stylesheet and only filled in after this test. The
     bars keep their blur, tint and rim everywhere; Chromium additionally bends the light at
     the bevel. Nothing is lost by failing this check.
     ------------------------------------------------------------------ */
  (function enableRefraction() {
    if (!window.CSS || !CSS.supports) return;
    var ok = CSS.supports('backdrop-filter', 'blur(1px) url(#bp-liquid)') ||
             CSS.supports('-webkit-backdrop-filter', 'blur(1px) url(#bp-liquid)');
    if (ok) document.documentElement.style.setProperty('--bp-bar-refract', 'url(#bp-liquid)');
  })();

  function init() {
    /* All three are ours now — #site-control is parked off-screen at every width, so it is
       not in this list any more; toggling a 1×1px invisible element did nothing. */
    var bars = [document.getElementById('bp-mtop'), document.getElementById('bp-mnav'),
                document.getElementById('bp-dheader')].filter(Boolean);
    var nav = document.getElementById('bp-mnav');
    var topBar = document.getElementById('bp-mtop');

    /* ==========================================================================
       TOP RESERVE — keep #page-content's padding equal to the pill's real height

       The pill is position:fixed, so it occupies no space in flow and the page would
       otherwise start underneath it. bp-mobile-nav.css reserves that space as padding on
       #page-content, computed from --bp-top-h.

       --bp-top-h starts at the 48px the stylesheet asks for, and is replaced here with
       what the bar MEASURED — because the bar can legitimately end up taller: a long
       search placeholder, a large iOS text-size setting, a translated label that wraps.
       Measuring means the reserve tracks those instead of being wrong by however much the
       bar grew.

       No oscillation: --bp-top-h feeds the bar's min-height, so writing back the measured
       height is a fixed point (48 → 48). The guard below skips writes that change nothing,
       which also keeps the ResizeObserver from re-entering.
       ========================================================================== */
    var lastTopH = null;
    function syncTopReserve() {
      if (!topBar) return;
      if (window.innerWidth > BREAK) {          /* desktop: the reserve isn't applied at all */
        lastTopH = null;
        document.documentElement.style.removeProperty('--bp-top-h');
        return;
      }
      var h = Math.round(topBar.getBoundingClientRect().height);
      /* 0 while the menu sheet or the search modal has the bar display:none. Keep the last
         good value rather than collapsing the whole page onto the chrome for a frame. */
      if (!h || h === lastTopH) return;
      lastTopH = h;
      document.documentElement.style.setProperty('--bp-top-h', h + 'px');
    }
    syncTopReserve();
    if (topBar && 'ResizeObserver' in window) {
      new ResizeObserver(syncTopReserve).observe(topBar);
    }
    /* webfonts land after first paint and can change the search row's height */
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(syncTopReserve);

    /* ==========================================================================
       THE MENU SHEET

       Ours (snippets/bp-menu.liquid), rendering section.settings.menu — the same link
       list the desktop header renders, which is the whole point: one setting in the
       editor, one menu on the store.

       The page lock is the same one the search overlay uses, and for the same reason:
       iOS Safari ignores `overflow: hidden` on the body, so the offset is captured here,
       held in `top` by bp-header.css while open, and handed back on close. Getting that
       pairing wrong teleports the customer to the top of the page when they close it.
       ========================================================================== */
    var sheet = document.getElementById('bp-menu');
    var menuLockedAt = 0;
    var menuLastFocus = null;

    function openMenu() {
      if (!sheet) return false;
      menuLastFocus = document.activeElement;
      menuLockedAt = window.pageYOffset || document.documentElement.scrollTop || 0;
      sheet.hidden = false;
      document.body.style.top = (-menuLockedAt) + 'px';
      document.body.classList.add('bp-menu-open');
      var close = sheet.querySelector('[data-bp-menu-close]');
      if (close && close.focus) close.focus({ preventScroll: true });
      return true;
    }

    function closeMenu() {
      if (!sheet || sheet.hidden) return;
      sheet.hidden = true;
      /* unlock BEFORE scrolling — the body has to be back in flow for scrollTo to land */
      document.body.classList.remove('bp-menu-open');
      document.body.style.top = '';
      window.scrollTo(0, menuLockedAt);
      if (menuLastFocus && menuLastFocus.focus) menuLastFocus.focus({ preventScroll: true });
    }

    if (sheet) {
      sheet.addEventListener('click', function (e) {
        if (e.target.closest('[data-bp-menu-close]')) { e.preventDefault(); closeMenu(); return; }

        var toggle = e.target.closest('[data-bp-menu-toggle]');
        if (toggle) {
          e.preventDefault();
          var open = toggle.getAttribute('aria-expanded') === 'true';
          toggle.setAttribute('aria-expanded', String(!open));
          var item = toggle.closest('.bp-menu__item');
          var sub = item && item.querySelector('[data-bp-menu-sub]');
          if (sub) sub.hidden = open;
          return;
        }

        /* A real link: let it navigate, but drop the lock first. Leaving the body fixed
           means a back-navigation restores this page unscrollable with nothing on screen
           to blame it on. */
        if (e.target.closest('a[href]')) closeMenu();
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !sheet.hidden) closeMenu();
      });

      /* bfcache can hand the page back with the sheet open and the body still pinned */
      window.addEventListener('pageshow', function (e) {
        if (e.persisted && !sheet.hidden) closeMenu();
      });
    }

    /* ---------- fallback: the theme's own sheet ---------- */
    function forward(selectors) {
      for (var i = 0; i < selectors.length; i++) {
        var el = document.querySelector(selectors[i]);
        if (el) { el.click(); return true; }
      }
      return false;
    }

    document.addEventListener('click', function (e) {
      var menu = e.target.closest('[data-bp-open-menu]');
      if (menu) {
        e.preventDefault();
        if (openMenu()) return;
        if (!forward(['#site-control a.menu', '[data-modal-nav-toggle]'])) {
          window.location.hash = '#page-menu';
        }
      }
    });

    /* ---------- instant tab feedback ----------
       The highlight moves the moment a tab is chosen, not when the next page arrives.
       On a prerendered page the swap is already instant and this is invisible; on a cold
       load (iOS, slow network) the old page stays on screen while the new one fetches,
       and an acknowledged tap is what separates "app" from "stuck".

       Bound to click, NOT touchstart: a touch that starts on the tab bar is just as often
       the beginning of a scroll, and moving the highlight on a gesture that never
       navigates would show the wrong page as active. Click only fires on a completed tap.

       No revert path is needed — the navigation replaces this document, and if it never
       comes (offline), the server-rendered state returns with the next real load. The new
       page renders its own .is-active, and since we moved ours first, the two bar
       snapshots agree and the pinned view-transition group swaps without a flicker. */
    if (nav) {
      nav.addEventListener('click', function (e) {
        var tab = e.target.closest('.bp-mnav__tab');
        if (!tab || tab.classList.contains('is-active')) return;
        var tabs = nav.querySelectorAll('.bp-mnav__tab');
        for (var i = 0; i < tabs.length; i++) {
          tabs[i].classList.remove('is-active');
          tabs[i].removeAttribute('aria-current');
        }
        tab.classList.add('is-active');
        tab.setAttribute('aria-current', 'page');
      });
    }

    /* ==========================================================================
       Corner widgets — the Shopify Inbox chat bubble

       There is no theme file to edit for this: app embed blocks are injected by
       Shopify right before </body>, wrapped in <div id="shopify-block-…">, and the app
       builds the launcher from its own script. So it is found by GEOMETRY, not by
       selector: a small fixed-position element pinned bottom-right that isn't ours.

       It must be searched for through the WHOLE body subtree — the earlier version only
       checked document.body.children and so never saw it inside the app-embed wrapper.

       Discovery (deep, expensive) is separated from positioning (cheap): the deep scan
       runs a handful of times after load and on DOM mutations, while scrolling only
       re-positions the elements already found.
       ========================================================================== */
    var OUR_IDS = ['bp-mnav', 'bp-mtop', 'bp-search', 'bp-menu', 'site-control', 'page-menu'];
    var found = [];

    function insideOurs(el) {
      for (var i = 0; i < OUR_IDS.length; i++) {
        var root = document.getElementById(OUR_IDS[i]);
        if (root && root.contains(el)) return true;
      }
      return !!(el.closest && el.closest('.bp-pdp__sticky-bar, .bp-mnav, .bp-mtop, .bp-search, .bp-menu'));
    }

    function isCornerWidget(el) {
      var cs = window.getComputedStyle(el);
      if (cs.position !== 'fixed') return false;
      if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') return false;

      var r = el.getBoundingClientRect();
      if (!r.width || !r.height) return false;
      if (r.width > 170 || r.height > 170) return false;                 /* launcher-sized, never a bar */
      if (r.bottom < window.innerHeight - 240) return false;             /* pinned near the bottom */
      if (r.right < window.innerWidth - 240) return false;               /* pinned near the right */
      return true;
    }

    function discover() {
      if (window.innerWidth > BREAK) return;
      var hits = [];
      var all = document.body.getElementsByTagName('*');
      for (var i = 0; i < all.length; i++) {
        var el = all[i];
        var tag = el.tagName;
        if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'LINK' || tag === 'NOSCRIPT') continue;
        if (insideOurs(el)) continue;
        if (!isCornerWidget(el)) continue;
        hits.push(el);
      }
      /* keep only the outermost match per widget, so we move the launcher, not its icon */
      hits.forEach(function (el) {
        var nested = hits.some(function (other) { return other !== el && other.contains(el); });
        if (!nested && found.indexOf(el) === -1) found.push(el);
      });
      if (hits.length) place();
    }

    /* how far up from the bottom edge a widget should sit — measured off the tab bar
       itself, so the raised product-page position needs no separate maths */
    function clearance() {
      if (window.innerWidth > BREAK) return null;      /* desktop: hand it back to the app */
      if (!nav) return null;
      if (nav.classList.contains(HIDDEN)) return 18;   /* chrome away → corner is free */
      var r = nav.getBoundingClientRect();
      if (!r.height) return null;
      return Math.max(18, Math.round(window.innerHeight - r.top) + 10);
    }

    function place() {
      var offset = clearance();
      found.forEach(function (el) {
        if (offset === null) {
          el.style.removeProperty('bottom');
          el.style.removeProperty('transition');
          return;
        }
        /* priority beats the inline style the app writes on itself */
        el.style.setProperty('transition', 'bottom .32s cubic-bezier(.16,1,.3,1)');
        el.style.setProperty('bottom', offset + 'px', 'important');
      });
    }

    function placeSoon() {
      place();
      setTimeout(place, 360);      /* the bars animate for 320ms — settle after they land */
    }

    /* ---------- hide on scroll down, show on scroll up ----------

       The bars move on INTENT, not on every stray pixel. The old version flipped as soon
       as 5px went by in either direction, so a thumb that wobbled mid-scroll made the
       chrome flicker in and out — which is most of what made the hiding feel bad.

       Instead, distance is accumulated in whichever direction you are going and the flip
       only happens once that adds up. The budgets are deliberately asymmetric: it takes
       a real downward scroll to send the chrome away, but only a small flick back up to
       call it in, because wanting it back is a deliberate act and should feel instant. */
    var HIDE_AFTER = 52;   /* px of sustained downward scroll before the bars leave */
    var SHOW_AFTER = 20;   /* px of upward scroll to bring them back */
    var BOTTOM_PAD = 140;  /* within this of the end of the page, always show */

    var last = window.pageYOffset || document.documentElement.scrollTop || 0;
    var travel = 0;
    var hidden = false;
    var ticking = false;

    function apply(hide) {
      if (hide === hidden) return;      /* no-op guard: repositioning widgets is not free */
      hidden = hide;
      for (var i = 0; i < bars.length; i++) bars[i].classList.toggle(HIDDEN, hide);
      document.body.classList.toggle('bp-chrome-down', hide);
      placeSoon();
    }

    function atEnd(y) {
      var doc = document.documentElement;
      return (y + window.innerHeight) >= (doc.scrollHeight - BOTTOM_PAD);
    }

    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        var y = window.pageYOffset || document.documentElement.scrollTop || 0;
        var diff = y - last;
        last = y;
        if (Math.abs(diff) < DELTA) return;              /* jitter / rubber-banding */

        /* near the top, and at the very end of the page, the chrome is always available */
        if (y <= THRESHOLD || atEnd(y)) { travel = 0; apply(false); return; }

        if ((diff > 0) !== (travel > 0)) travel = 0;     /* turned around — start counting again */
        travel += diff;

        if (travel > HIDE_AFTER) apply(true);
        else if (travel < -SHOW_AFTER) apply(false);
      });
    }, { passive: true });

    /* back/forward restore: land with the chrome present and the counter clean */
    window.addEventListener('pageshow', function () {
      travel = 0;
      last = window.pageYOffset || document.documentElement.scrollTop || 0;
      apply(false);
    });
    window.addEventListener('resize', function () {
      /* Crossing to desktop with the sheet open would leave the body locked behind a
         panel the desktop layout never shows. */
      if (window.innerWidth > BREAK) closeMenu();
      syncTopReserve();               /* rotation changes the bar's width, and can its height */
      discover();
      placeSoon();
    }, { passive: true });

    /* Publish the product page's Add-to-cart bar height so the tab bar can sit exactly
       on top of it (see --bp-atc-h in bp-mobile-nav.css). Measured rather than assumed:
       the bar grows with a long product title and the safe-area inset. */
    var atc = document.querySelector('[data-bp-sticky-bar], .bp-pdp__sticky-bar');
    if (atc) {
      var measure = function () {
        var h = atc.offsetHeight;
        if (h) document.documentElement.style.setProperty('--bp-atc-h', h + 'px');
        placeSoon();                        /* buy bar in or out → widget follows */
      };
      measure();
      window.addEventListener('resize', measure, { passive: true });
      if ('MutationObserver' in window) {
        new MutationObserver(measure).observe(atc, { attributes: true, attributeFilter: ['class'] });
      }
    }

    /* The chat script loads long after us and re-renders itself, so keep looking for a
       while rather than assuming it exists at DOMContentLoaded.

       Deferred while PRERENDERED: bp-instant.js prerenders the other tab pages in the
       background, and this full-DOM scan is the one genuinely expensive thing this file
       does. Running it five times inside an invisible page wastes the very budget the
       prerender is meant to save — and finds nothing, because the chat widget defers
       itself to first interaction, which a prerendered page cannot have yet. Kick off on
       activation instead; everything above is event-driven and stays inert while hidden. */
    function kickoffDiscovery() {
      discover();
      [400, 1200, 2500, 5000, 9000].forEach(function (t) { setTimeout(discover, t); });

      if ('MutationObserver' in window) {
        var pending = null;
        new MutationObserver(function () {
          clearTimeout(pending);
          pending = setTimeout(discover, 400);   /* debounced: the deep scan is the costly half */
        }).observe(document.body, { childList: true, subtree: true });
      }
    }
    if (document.prerendering) {
      document.addEventListener('prerenderingchange', kickoffDiscovery, { once: true });
    } else {
      kickoffDiscovery();
    }

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') { discover(); placeSoon(); }
    });

    /* let me check from the console when something still looks wrong */
    window.bpChrome = {
      discover: discover, place: place, found: found, clearance: clearance,
      syncTopReserve: syncTopReserve, openMenu: openMenu, closeMenu: closeMenu
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
